import { useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: ReactNode;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary';
  hint?: string;
  to?: string;
  loading?: boolean;
}

export function StatCard({
  title,
  value,
  icon,
  color = 'primary',
  hint,
  to,
  loading = false,
}: StatCardProps) {
  const navigate = useNavigate();

  const content = (
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1.5,
            bgcolor: `${color}.main`,
            color: `${color}.contrastText`,
            flexShrink: 0,
          }}
        >
          {icon}
        </Stack>
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" noWrap>
            {title}
          </Typography>
          {loading ? (
            <Skeleton variant="text" width={90} height={34} />
          ) : (
            <Typography variant="h5" sx={{ lineHeight: 1.3 }}>
              {value}
            </Typography>
          )}
          {hint ? (
            <Typography variant="caption" color="text.secondary">
              {hint}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </CardContent>
  );

  return (
    <Card sx={{ height: '100%' }}>
      {to ? (
        <CardActionArea onClick={() => navigate(to)} sx={{ height: '100%' }}>
          {content}
        </CardActionArea>
      ) : (
        content
      )}
    </Card>
  );
}
